/**
 * FloorPlanCanvas — reusable canvas overlay for interactive maps.
 * Props:
 *  - imageUrl: string (absolute or asset path)
 *  - devices: array of pest devices
 *  - areas: array of named areas
 *  - mode: "view" | "place-device" | "place-area" | "edit-devices"
 *  - onDeviceClick(device): fn
 *  - onAreaClick(area): fn
 *  - onMapClick(x%, y%): fn (when mode=place-device)
 *  - onAreaDrawn({x,y,w,h}): fn (when mode=place-area, after drag)
 *  - onDeviceMove(deviceId, x%, y%): fn (drag end)
 *  - draggable: bool (devices)
 *  - showLabels: bool
 *  - statusByDeviceId: {[id]: "ok"|"warning"|"alert"} (overrides marker color)
 *  - className: optional
 */
import React, { useRef, useState, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { assetUrl } from "@/lib/api";

const ZONE_COLOR = {
    verde: "#16a34a",
    amarilla: "#eab308",
    roja: "#dc2626",
};
// UV lamps use violet to distinguish from red traps in the same zone
const UV_COLOR = "#9333ea";

// Distinct colors per subtype so each device kind is visually unique on the map
const SUBTYPE_COLOR = {
    // Cebaderos
    caja: "#1a1a1a",     // negro — cebadero de caja
    tubo: "#1e40af",     // azul oscuro — cebadero de tubo
    roca: "#92400e",     // marrón/café — cebadero roca
    // Trampas
    gato_papel: "#dc2626",  // rojo — gato papel
    bandeja: "#f97316",     // naranja — bandeja
    multicaptura: "#16a34a", // verde — multicaptura
    trex: "#0891b2",        // cyan — T-Rex
    trampa_paso: "#db2777", // magenta — trampa de paso
};
// Distinct halo colors for inspection status (different from zone colors)
const STATUS_RING = {
    ok: "#059669",       // emerald — distinct from zone green
    warning: "#f97316",  // orange — distinct from zone yellow
    alert: "#7f1d1d",    // dark crimson — distinct from zone red
};
const TYPE_ICON = {
    cebadero_cebo: "C",   // Cebadero
    trampa: "T",
    lampara_uv: "U",
};
const TRAP_SUBTYPE_LBL = {
    gato_papel: "Gato papel",
    bandeja: "Bandeja",
    multicaptura: "Multicaptura",
    trex: "T-Rex",
    trampa_paso: "Trampa de paso",
    caja: "Cebadero de caja",
    tubo: "Cebadero de tubo",
    roca: "Cebadero roca",
};

export default function FloorPlanCanvas({
    imageUrl,
    devices = [],
    areas = [],
    mode = "view",
    onDeviceClick,
    onDeviceDoubleClick,
    onAreaClick,
    onMapClick,
    onAreaDrawn,
    onDeviceMove,
    draggable = false,
    showLabels = true,
    statusByDeviceId = {},
    areaStatusByAreaId = {},
    installMode = false,
    highlightedDeviceId = null,
    controlledZoom = null,
    onZoomChange = null,
    hideZoomControls = false,
    draftArea = null,
    onDraftAreaChange = null,
    onDraftAreaConfirm = null,
    className = "",
}) {
    const wrapperRef = useRef(null);
    const outerRef = useRef(null);
    const dragRef = useRef(null); // {deviceId, currentX, currentY}
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
    const [containerWidth, setContainerWidth] = useState(0);
    // Marker size proportional to rendered plan width (keeps markers visually
    // consistent between wide (admin) and narrow (tech mobile) renders).
    const markerPx = Math.max(16, Math.min(38, containerWidth * 0.045));
    const badgePx = Math.max(9, markerPx * 0.5);
    const [isDragging, setIsDragging] = useState(false);
    const [drawing, setDrawing] = useState(null); // {x,y,w,h} during area draw
    const [internalZoom, setInternalZoom] = useState(1);
    const zoom = controlledZoom !== null ? controlledZoom : internalZoom;
    const setZoom = (next) => {
        const v = typeof next === "function" ? next(zoom) : next;
        if (onZoomChange) onZoomChange(v);
        if (controlledZoom === null) setInternalZoom(v);
    };

    const ZOOM_MIN = 0.5;
    const ZOOM_MAX = 4;
    const ZOOM_STEP = 0.25;

    // Track the rendered plan width so markers can be sized proportionally.
    useEffect(() => {
        const el = outerRef.current;
        if (!el) return;
        const update = () => setContainerWidth(el.getBoundingClientRect().width);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Auto-zoom + pan to highlighted device
    useEffect(() => {
        if (!highlightedDeviceId) return;
        const dev = devices.find((d) => d.id === highlightedDeviceId);
        if (!dev) return;
        // Bring zoom to at least 1.5
        setZoom((z) => z < 1.5 ? 1.5 : z);
        // Wait a tick for layout, then scroll
        const t = setTimeout(() => {
            const outer = outerRef.current;
            const wrap = wrapperRef.current;
            if (!outer || !wrap) return;
            const wrapRect = wrap.getBoundingClientRect();
            const targetX = (dev.position_x / 100) * wrapRect.width;
            const targetY = (dev.position_y / 100) * wrapRect.height;
            const outerRect = outer.getBoundingClientRect();
            // Only outer is scrollable when zoom > 1
            outer.scrollTo({
                left: Math.max(0, targetX - outerRect.width / 2),
                top: Math.max(0, targetY - outerRect.height / 2),
                behavior: "smooth",
            });
        }, 120);
        return () => clearTimeout(t);
    }, [highlightedDeviceId, devices]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleZoomIn = (e) => {
        e?.stopPropagation();
        setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
    };
    const handleZoomOut = (e) => {
        e?.stopPropagation();
        setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
    };
    const handleZoomReset = (e) => {
        e?.stopPropagation();
        setZoom(1);
    };

    // Ctrl+wheel to zoom (desktop)
    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            setZoom((z) => {
                const next = e.deltaY < 0 ? z + ZOOM_STEP : z - ZOOM_STEP;
                return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, +next.toFixed(2)));
            });
        }
    };

    const handleImgLoad = (e) => {
        setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    };

    const getRelativeXY = (clientX, clientY) => {
        const rect = wrapperRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
        return { x, y };
    };

    const handleMapPointerDown = (e) => {
        if (mode === "place-area") {
            const { x, y } = getRelativeXY(e.clientX, e.clientY);
            setDrawing({ x, y, w: 0, h: 0 });
        }
    };

    const handleMapPointerMove = (e) => {
        if (mode === "place-area" && drawing) {
            const { x, y } = getRelativeXY(e.clientX, e.clientY);
            setDrawing((d) => ({
                x: Math.min(d.x, x),
                y: Math.min(d.y, y),
                w: Math.abs(x - d.x),
                h: Math.abs(y - d.y),
            }));
        }
        if (dragRef.current) {
            const { x, y } = getRelativeXY(e.clientX, e.clientY);
            dragRef.current.currentX = x;
            dragRef.current.currentY = y;
            const el = wrapperRef.current?.querySelector(`[data-marker-id="${dragRef.current.deviceId}"]`);
            if (el) {
                el.style.left = `${x}%`;
                el.style.top = `${y}%`;
            }
        }
    };

    const handleMapPointerUp = (e) => {
        if (mode === "place-area" && drawing) {
            if (drawing.w > 1 && drawing.h > 1) {
                onAreaDrawn?.(drawing);
            }
            setDrawing(null);
            return;
        }
        if (dragRef.current) {
            const { x, y } = getRelativeXY(e.clientX, e.clientY);
            const id = dragRef.current.deviceId;
            dragRef.current = null;
            setIsDragging(false);
            onDeviceMove?.(id, x, y);
            return;
        }
        if (mode === "place-device" || onMapClick) {
            const { x, y } = getRelativeXY(e.clientX, e.clientY);
            onMapClick?.(x, y);
        }
    };

    const handleDevicePointerDown = (e, d) => {
        if (draggable) {
            e.stopPropagation();
            dragRef.current = { deviceId: d.id, currentX: d.position_x, currentY: d.position_y };
            setIsDragging(true);
        }
    };

    const clickTimerRef = useRef(null);

    const handleDeviceClick = (e, d) => {
        e.stopPropagation();
        if (isDragging) return;
        if (onDeviceDoubleClick && d.device_type === "trampa") {
            // Delay single-click to allow double-click detection
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
            clickTimerRef.current = setTimeout(() => { onDeviceClick?.(d); }, 250);
        } else {
            onDeviceClick?.(d);
        }
    };

    const handleDeviceDoubleClick = (e, d) => {
        e.stopPropagation();
        e.preventDefault();
        if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
        onDeviceDoubleClick?.(d);
    };

    return (
        <div
            ref={outerRef}
            className={`relative bg-slate-50 ${className}`}
            style={{
                overflow: zoom > 1 ? "auto" : "visible",
                maxHeight: zoom > 1 ? "75vh" : "none",
            }}
            onWheel={handleWheel}
        >
            {/* Floating zoom controls — overlay sobre el plano, esquina superior derecha */}
            {!hideZoomControls && (
            <div className="absolute top-2 right-2 z-20 flex flex-col gap-1 bg-white/90 backdrop-blur border rounded shadow-sm p-1">
                <button
                    onClick={handleZoomIn}
                    title="Acercar (Ctrl + scroll)"
                    className="p-1 hover:bg-slate-100 rounded"
                    data-testid="map-zoom-in"
                >
                    <ZoomIn className="w-4 h-4" />
                </button>
                <button
                    onClick={handleZoomReset}
                    title="Tamaño original"
                    className="p-1 hover:bg-slate-100 rounded text-[10px] font-mono leading-none flex items-center justify-center"
                    data-testid="map-zoom-reset"
                >
                    {Math.round(zoom * 100)}%
                </button>
                <button
                    onClick={handleZoomOut}
                    title="Alejar"
                    className="p-1 hover:bg-slate-100 rounded"
                    data-testid="map-zoom-out"
                >
                    <ZoomOut className="w-4 h-4" />
                </button>
            </div>
            )}

            <div
                ref={wrapperRef}
                className="relative select-none touch-pan-x touch-pan-y inline-block"
                style={{
                    width: `${zoom * 100}%`,
                    cursor: mode === "place-device" ? "crosshair" :
                            mode === "place-area" ? "crosshair" : "default",
                    userSelect: "none",
                    fontSize: 0, /* collapse inline whitespace so wrapper matches image exactly */
                }}
                onPointerDown={handleMapPointerDown}
                onPointerMove={handleMapPointerMove}
                onPointerUp={handleMapPointerUp}
                data-testid="floor-plan-canvas"
            >
            {imageUrl?.toLowerCase().endsWith(".pdf") ? (
                <iframe
                    src={assetUrl(imageUrl)}
                    title="plano"
                    className="w-full pointer-events-none"
                    style={{ minHeight: "600px", border: "none" }}
                />
            ) : (
                <img
                    src={assetUrl(imageUrl)}
                    alt="plano"
                    onLoad={handleImgLoad}
                    draggable={false}
                    className="w-full h-auto block pointer-events-none"
                    style={{ maxWidth: "100%" }}
                />
            )}

            {/* AREAS — translucent rectangles with labels (supports multi-rect L-shapes) */}
            {areas.map((a) => {
                const aStatus = areaStatusByAreaId[a.area_id];
                let border = "border-blue-500/70", bg = "bg-blue-500/15", hoverBg = "hover:bg-blue-500/25", label = "bg-blue-600";
                if (aStatus === "warning") { border = "border-orange-500"; bg = "bg-orange-500/20"; hoverBg = "hover:bg-orange-500/30"; label = "bg-orange-600"; }
                else if (aStatus === "alert") { border = "border-red-600"; bg = "bg-red-500/25"; hoverBg = "hover:bg-red-500/35"; label = "bg-red-700"; }
                // All rects for this area: primary + additional
                const allRects = [{ x: a.x, y: a.y, w: a.w, h: a.h }, ...(a.rects || [])];
                return (
                <React.Fragment key={a.area_id}>
                    {allRects.map((r, ri) => (
                        <div
                            key={`${a.area_id}-r${ri}`}
                            className={`absolute ${bg} ${hoverBg} transition-colors cursor-pointer`}
                            style={{
                                left: `${r.x}%`,
                                top: `${r.y}%`,
                                width: `${r.w}%`,
                                height: `${r.h}%`,
                                fontSize: "initial",
                            }}
                            onClick={(e) => { e.stopPropagation(); onAreaClick?.(a); }}
                            data-testid={ri === 0 ? `area-${a.area_id}` : undefined}
                        >
                            {showLabels && ri === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <span className={`${label} text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap opacity-80`}>
                                        {a.name}
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                </React.Fragment>
                );
            })}

            {/* AREA being drawn */}
            {drawing && (
                <div
                    className="absolute border-2 border-blue-700 bg-blue-500/30 pointer-events-none"
                    style={{
                        left: `${drawing.x}%`,
                        top: `${drawing.y}%`,
                        width: `${drawing.w}%`,
                        height: `${drawing.h}%`,
                    }}
                />
            )}

            {/* DEVICE MARKERS */}
            {devices.map((d) => {
                const status = statusByDeviceId[d.id];
                // Marker base color: subtype-specific if available, else zone/UV fallback
                const baseColor = d.device_type === "lampara_uv"
                    ? UV_COLOR
                    : (d.trap_subtype && SUBTYPE_COLOR[d.trap_subtype])
                        ? SUBTYPE_COLOR[d.trap_subtype]
                        : ZONE_COLOR[d.zone];
                const ringColor = status ? STATUS_RING[status] : null;
                const subtypeLbl = d.trap_subtype ? TRAP_SUBTYPE_LBL[d.trap_subtype] : "";
                const tooltip = [d.device_number, subtypeLbl, d.area_name, d.qr_status === "pending" ? "⏳ Pendiente de QR" : ""].filter(Boolean).join(" · ");
                const isHighlighted = highlightedDeviceId === d.id;
                const isInstalled = !!d.installation_date;
                const isPendingQr = d.qr_status === "pending";
                // Dim uninstalled devices ALWAYS so the tech sees at a glance which aren't ready
                const dimmed = !isInstalled && !isPendingQr;
                return (
                    <div
                        key={d.id}
                        data-marker-id={d.id}
                        data-testid={`device-marker-${d.device_number}`}
                        title={tooltip}
                        className="absolute flex flex-col items-center hover:scale-110 transition-transform"
                        style={{
                            left: `${d.position_x}%`,
                            top: `${d.position_y}%`,
                            transform: `translate(-50%, -50%) scale(${zoom})`,
                            transformOrigin: "center center",
                            fontSize: "initial",
                            zIndex: isHighlighted ? 30 : 5,
                            cursor: draggable ? "grab" : (onDeviceClick ? "pointer" : "default"),
                        }}
                        onPointerDown={(e) => handleDevicePointerDown(e, d)}
                        onClick={(e) => handleDeviceClick(e, d)}
                        onDoubleClick={(e) => handleDeviceDoubleClick(e, d)}
                    >
                        {/* Pulse ring when highlighted */}
                        {isHighlighted && (
                            <span
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full animate-ping"
                                style={{ width: markerPx, height: markerPx, background: baseColor, opacity: 0.5, animationDuration: "1.2s" }}
                                aria-hidden
                            />
                        )}
                        <svg width={markerPx} height={markerPx} viewBox="0 0 28 28" className="relative" style={{ opacity: dimmed ? 0.45 : 1, filter: `drop-shadow(0 1px 2px rgba(0,0,0,0.3))`, transform: d.device_type === "trampa" && (d.rotation || (d.rotated ? 180 : 0)) ? `rotate(${d.rotation || (d.rotated ? 180 : 0)}deg)` : undefined }}>
                            {d.device_type === "cebadero_cebo" ? (
                                d.trap_subtype === "tubo" ? (
                                    /* Rectangle for tubo */
                                    <rect x="1" y="7" width="26" height="14" rx="2" fill={baseColor} stroke="white" strokeWidth="2" />
                                ) : d.trap_subtype === "roca" ? (
                                    /* Circle for roca */
                                    <circle cx="14" cy="14" r="12" fill={baseColor} stroke="white" strokeWidth="2" />
                                ) : (
                                    /* Square for caja (default) */
                                    <rect x="2" y="2" width="24" height="24" rx="3" fill={baseColor} stroke="white" strokeWidth="2" />
                                )
                            ) : d.device_type === "lampara_uv" ? (
                                /* Diamond for UV lamps */
                                <polygon points="14,1 27,14 14,27 1,14" fill={baseColor} stroke="white" strokeWidth="2" />
                            ) : (
                                /* Triangle for traps */
                                <polygon points="14,2 26,26 2,26" fill={baseColor} stroke="white" strokeWidth="2" />
                            )}
                            <text x="14" y={d.device_type === "trampa" ? "20" : "16"} textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" style={{ pointerEvents: "none", transform: d.device_type === "trampa" && (d.rotation || (d.rotated ? 180 : 0)) ? `rotate(${d.rotation || (d.rotated ? 180 : 0)}deg)` : undefined, transformOrigin: "14px 14px" }}>
                                {TYPE_ICON[d.device_type] || "?"}
                            </text>
                            {/* Outer ring for inspection status */}
                            {ringColor && !isHighlighted && (
                                d.device_type === "cebadero_cebo" ? (
                                    d.trap_subtype === "tubo" ? (
                                        <rect x="-1" y="5" width="30" height="18" rx="3" fill="none" stroke={ringColor} strokeWidth="3" />
                                    ) : d.trap_subtype === "roca" ? (
                                        <circle cx="14" cy="14" r="14" fill="none" stroke={ringColor} strokeWidth="3" />
                                    ) : (
                                        <rect x="0" y="0" width="28" height="28" rx="4" fill="none" stroke={ringColor} strokeWidth="3" />
                                    )
                                ) : d.device_type === "lampara_uv" ? (
                                    <polygon points="14,-1 29,14 14,29 -1,14" fill="none" stroke={ringColor} strokeWidth="3" />
                                ) : (
                                    <polygon points="14,0 28,28 0,28" fill="none" stroke={ringColor} strokeWidth="3" />
                                )
                            )}
                            {isHighlighted && (
                                <rect x="-2" y="-2" width="32" height="32" rx="6" fill="none" stroke={baseColor} strokeWidth="2" opacity="0.5" />
                            )}
                        </svg>
                        {/* Install check badge */}
                        <div className="relative" style={{ marginTop: `-${markerPx}px`, width: `${markerPx}px`, height: `${markerPx}px`, pointerEvents: "none" }}>
                            {installMode && isInstalled && (
                                <span
                                    className="absolute -top-1 -left-1 bg-green-600 border border-white rounded-full flex items-center justify-center"
                                    style={{ width: badgePx, height: badgePx }}
                                    aria-label="instalado"
                                >
                                    <svg width={badgePx * 0.55} height={badgePx * 0.55} viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" fill="none" stroke="white" strokeWidth="2" /></svg>
                                </span>
                            )}
                            {status && (
                                <span
                                    className="absolute -top-1 -right-1 border border-white rounded-full flex items-center justify-center font-bold text-white"
                                    style={{ width: badgePx, height: badgePx, fontSize: badgePx * 0.6, background: STATUS_RING[status] }}
                                    aria-label={status}
                                >
                                    {status === "ok" ? "✓" : status === "warning" ? "!" : "✕"}
                                </span>
                            )}
                        </div>
                        {showLabels && (
                            <div
                                className="text-[9px] bg-black/75 text-white px-1 py-0.5 rounded whitespace-nowrap"
                                style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: "2px" }}
                            >
                                {d.device_number}
                                {isPendingQr && <span className="ml-1 text-orange-300">⏳QR</span>}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* DRAFT AREA — movable + resizable rectangle (for the tech "Nombrar área" flow) */}
            {draftArea && (
                <div
                    className="absolute border-2 border-blue-600 bg-blue-500/20"
                    style={{
                        left: `${draftArea.x}%`,
                        top: `${draftArea.y}%`,
                        width: `${draftArea.w}%`,
                        height: `${draftArea.h}%`,
                        zIndex: 25,
                    }}
                    data-testid="draft-area"
                >
                    {/* Move handle (body) */}
                    <div
                        className="absolute inset-0 cursor-move touch-none flex items-center justify-center"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            e.currentTarget.setPointerCapture?.(e.pointerId);
                            const rect = wrapperRef.current.getBoundingClientRect();
                            const startPx = e.clientX, startPy = e.clientY;
                            const startX = draftArea.x, startY = draftArea.y;
                            const move = (ev) => {
                                const dx = ((ev.clientX - startPx) / rect.width) * 100;
                                const dy = ((ev.clientY - startPy) / rect.height) * 100;
                                const nx = Math.max(0, Math.min(100 - draftArea.w, startX + dx));
                                const ny = Math.max(0, Math.min(100 - draftArea.h, startY + dy));
                                onDraftAreaChange?.({ ...draftArea, x: nx, y: ny });
                            };
                            const up = () => {
                                window.removeEventListener("pointermove", move);
                                window.removeEventListener("pointerup", up);
                            };
                            window.addEventListener("pointermove", move);
                            window.addEventListener("pointerup", up);
                        }}
                        onClick={(e) => { e.stopPropagation(); onDraftAreaConfirm?.(); }}
                    >
                        <span className="bg-white/90 text-blue-700 text-[10px] font-semibold px-2 py-1 rounded shadow border border-blue-300 select-none">
                            Toca aquí para continuar
                        </span>
                    </div>
                    {/* Bottom-right resize handle */}
                    <div
                        className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 cursor-se-resize touch-none rounded-tl-md flex items-center justify-center"
                        style={{ transform: "translate(35%, 35%)" }}
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            e.currentTarget.setPointerCapture?.(e.pointerId);
                            const rect = wrapperRef.current.getBoundingClientRect();
                            const startPx = e.clientX, startPy = e.clientY;
                            const startW = draftArea.w, startH = draftArea.h;
                            const move = (ev) => {
                                const dw = ((ev.clientX - startPx) / rect.width) * 100;
                                const dh = ((ev.clientY - startPy) / rect.height) * 100;
                                const nw = Math.max(5, Math.min(100 - draftArea.x, startW + dw));
                                const nh = Math.max(5, Math.min(100 - draftArea.y, startH + dh));
                                onDraftAreaChange?.({ ...draftArea, w: nw, h: nh });
                            };
                            const up = () => {
                                window.removeEventListener("pointermove", move);
                                window.removeEventListener("pointerup", up);
                            };
                            window.addEventListener("pointermove", move);
                            window.addEventListener("pointerup", up);
                        }}
                        aria-label="redimensionar"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2 12L12 2M6 12L12 6M10 12L12 10" stroke="white" strokeWidth="1.5" />
                        </svg>
                    </div>
                </div>
            )}

            {!imageUrl && (
                <div className="text-center text-muted-foreground py-12">
                    No hay plano cargado
                </div>
            )}
            </div>{/* end inner zoom wrapper */}
        </div>
    );
}
